"use client";

import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { CreditCard, ShieldCheck } from "lucide-react";
import Script from "next/script";
import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { createClientId } from "@/lib/client-id";

type ReservationPaymentSessionBase = {
  publicId: string;
  sessionToken: string;
  objectType: "payment_intent" | "setup_intent";
  bookingMethod: "deposit" | "prepaid" | "card_hold";
  amount: number;
  currency: string;
};

export type ReservationPaymentSession = ReservationPaymentSessionBase & (
  | { provider?: "stripe"; clientSecret: string; publishableKey: string }
  | { provider: "finix"; applicationId: string; environment: "sandbox" | "prod" }
);

export type SavedPaymentMethod = {
  publicId: string;
  brand: string;
  last4: string;
  expMonth?: number;
  expYear?: number;
  isDefault: boolean;
};

export function StripePaymentStep({
  session,
  onBack,
  onComplete,
  context = "public",
  savedPaymentMethods = [],
  verifyEndpoint,
}: {
  session: ReservationPaymentSession;
  onBack: () => void;
  onComplete: () => Promise<void>;
  context?: "public" | "staff";
  savedPaymentMethods?: SavedPaymentMethod[];
  verifyEndpoint?: string;
}) {
  if (session.provider === "finix") {
    return (
      <FinixPaymentForm
        session={session}
        onBack={onBack}
        onComplete={onComplete}
        context={context}
        savedPaymentMethods={savedPaymentMethods}
        verifyEndpoint={verifyEndpoint}
      />
    );
  }
  return (
    <StripePaymentElementsStep
      session={session}
      onBack={onBack}
      onComplete={onComplete}
      context={context}
      savedPaymentMethods={savedPaymentMethods}
      verifyEndpoint={verifyEndpoint}
    />
  );
}

function StripePaymentElementsStep({
  session,
  onBack,
  onComplete,
  context,
  savedPaymentMethods,
  verifyEndpoint,
}: {
  session: Extract<ReservationPaymentSession, { provider?: "stripe" }>;
  onBack: () => void;
  onComplete: () => Promise<void>;
  context: "public" | "staff";
  savedPaymentMethods: SavedPaymentMethod[];
  verifyEndpoint?: string;
}) {
  const stripe = useMemo(
    () => loadStripe(session.publishableKey),
    [session.publishableKey],
  );
  return (
    <Elements
      stripe={stripe}
      options={{
        clientSecret: session.clientSecret,
        appearance: {
          theme: "stripe",
          variables: {
            colorPrimary: "#721121",
            borderRadius: "6px",
            fontFamily: "Arial, sans-serif",
          },
        },
      }}
    >
      <StripePaymentForm
        session={session}
        onBack={onBack}
        onComplete={onComplete}
        context={context}
        savedPaymentMethods={savedPaymentMethods}
        verifyEndpoint={verifyEndpoint}
      />
    </Elements>
  );
}

type FinixPaymentFormInstance = {
  submit(callback: (error: { message?: string; data?: { message?: string } } | null, response: { data?: { id?: string } } | null) => void): void;
};

declare global {
  interface Window {
    Finix?: {
      PaymentForm(
        element: string | HTMLElement,
        environment: "sandbox" | "prod",
        applicationId: string,
        options: {
          paymentMethods: string[];
          showAddress: boolean;
          onLoad(): void;
          onUpdate(state: Record<string, unknown>, binInformation: Record<string, unknown>, hasErrors: boolean): void;
        },
      ): FinixPaymentFormInstance;
    };
  }
}

function FinixPaymentForm({
  session,
  onBack,
  onComplete,
  context,
  savedPaymentMethods,
  verifyEndpoint,
}: {
  session: Extract<ReservationPaymentSession, { provider: "finix" }>;
  onBack: () => void;
  onComplete: () => Promise<void>;
  context: "public" | "staff";
  savedPaymentMethods: SavedPaymentMethod[];
  verifyEndpoint?: string;
}) {
  const mountRef = useRef<HTMLDivElement>(null);
  const formRef = useRef<FinixPaymentFormInstance | undefined>(undefined);
  const [sdkReady, setSdkReady] = useState(false);
  const [formReady, setFormReady] = useState(false);
  const [formHasErrors, setFormHasErrors] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const isCardHold = session.objectType === "setup_intent";

  useEffect(() => {
    if (!sdkReady || !window.Finix || !mountRef.current || formRef.current) return;
    formRef.current = window.Finix.PaymentForm(
      mountRef.current,
      session.environment,
      session.applicationId,
      {
        paymentMethods: ["card"],
        showAddress: true,
        onLoad: () => setFormReady(true),
        onUpdate: (_state, _binInformation, hasErrors) => setFormHasErrors(hasErrors),
      },
    );
  }, [sdkReady, session.applicationId, session.environment]);

  const finishSavedMethod = async (paymentMethod: SavedPaymentMethod) => {
    setSubmitting(true);
    setError("");
    try {
      const response = await fetch(
        `/api/tables/account/payment-sessions/${session.publicId}/confirm-saved-method`,
        {
          method: "POST",
          headers: { "content-type": "application/json", "idempotency-key": createClientId() },
          body: JSON.stringify({
            sessionToken: session.sessionToken,
            paymentMethodPublicId: paymentMethod.publicId,
          }),
        },
      );
      const payload = await response.json();
      if (!response.ok || payload.status !== "succeeded")
        throw new Error(payload.detail ?? payload.message ?? "The saved card could not be used.");
      await onComplete();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "The saved card could not be used.");
      setSubmitting(false);
    }
  };

  const submit = async () => {
    if (!formRef.current) return;
    setSubmitting(true);
    setError("");
    try {
      const token = await new Promise<string>((resolve, reject) => {
        formRef.current!.submit((tokenError, response) => {
          const tokenId = response?.data?.id;
          if (tokenError || !tokenId) {
            reject(new Error(tokenError?.message ?? tokenError?.data?.message ?? "Check the card details and try again."));
            return;
          }
          resolve(tokenId);
        });
      });
      const confirmEndpoint = verifyEndpoint
        ? verifyEndpoint.replace(/\/verify$/, "/confirm-token")
        : `/api/tables/payment-sessions/${session.publicId}/confirm-token`;
      const response = await fetch(confirmEndpoint, {
        method: "POST",
        headers: { "content-type": "application/json", "idempotency-key": createClientId() },
        body: JSON.stringify({ sessionToken: session.sessionToken, token }),
      });
      const payload = await response.json();
      if (!response.ok || payload.status !== "succeeded")
        throw new Error(payload.detail ?? payload.message ?? "Trellis Payments is still processing this payment.");
      await onComplete();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "The payment could not be completed.");
      setSubmitting(false);
    }
  };

  return (
    <div className={`booking-body booking-details stripe-payment-step reservation-payment-step ${context}`}>
      <Script
        id="trellis-tables-finix-sdk"
        src="https://js.finix.com/v/2/finix.js"
        strategy="afterInteractive"
        onReady={() => setSdkReady(true)}
        onError={() => setError("The secure payment form could not load.")}
      />
      <p className="step-label">Secure payment</p>
      <h2>{isCardHold ? "Save a card for this reservation." : "Complete the payment."}</h2>
      <div className="stripe-payment-summary">
        <CreditCard />
        <span>
          <strong>{isCardHold ? "Card hold authorization" : session.bookingMethod === "deposit" ? "Reservation deposit" : "Prepaid reservation"}</strong>
          <small>{formatMoney(session.amount, session.currency)} · processed by Trellis Payments</small>
        </span>
      </div>
      {savedPaymentMethods.length > 0 && (
        <div className="saved-payment-options">
          <strong>Use a saved card</strong>
          {savedPaymentMethods.map((method) => (
            <button type="button" key={method.publicId} disabled={submitting} onClick={() => void finishSavedMethod(method)}>
              <CreditCard />
              <span><b>{method.brand} •••• {method.last4}</b><small>{method.expMonth && method.expYear ? `Expires ${String(method.expMonth).padStart(2, "0")}/${String(method.expYear).slice(-2)}` : "Saved securely"}</small></span>
              <em>{method.isDefault ? "Default" : "Use"}</em>
            </button>
          ))}
          <span>Or enter another card</span>
        </div>
      )}
      <div ref={mountRef} className="finix-payment-form" />
      {isCardHold && (
        <p className="stripe-payment-disclosure">
          <ShieldCheck /> The card is saved securely and is not charged now. The venue may charge up to {formatMoney(session.amount, session.currency)} under the reservation policy.
        </p>
      )}
      {error && <p className="booking-error" role="alert">{error}</p>}
      <Button
        size="lg"
        className="button button--burgundy button--full"
        disabled={!formReady || formHasErrors || submitting}
        onClick={() => void submit()}
      >
        {submitting ? "Confirming with Trellis Payments…" : isCardHold ? "Save card and confirm" : `Pay ${formatMoney(session.amount, session.currency)} and confirm`}
      </Button>
      <button className="confirmation-done" type="button" disabled={submitting} onClick={onBack}>Back to payment options</button>
    </div>
  );
}

function StripePaymentForm({
  session,
  onBack,
  onComplete,
  context,
  savedPaymentMethods,
  verifyEndpoint,
}: {
  session: ReservationPaymentSession;
  onBack: () => void;
  onComplete: () => Promise<void>;
  context: "public" | "staff";
  savedPaymentMethods: SavedPaymentMethod[];
  verifyEndpoint?: string;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const isCardHold = session.objectType === "setup_intent";

  const submitSavedMethod = async (paymentMethod: SavedPaymentMethod) => {
    if (!stripe) return;
    setSubmitting(true);
    setError("");
    try {
      const response = await fetch(
        `/api/tables/account/payment-sessions/${session.publicId}/confirm-saved-method`,
        {
          method: "POST",
          headers: {
            "content-type": "application/json",
            "idempotency-key": createClientId(),
          },
          body: JSON.stringify({
            sessionToken: session.sessionToken,
            paymentMethodPublicId: paymentMethod.publicId,
          }),
        },
      );
      const payload = await response.json();
      if (!response.ok)
        throw new Error(payload.detail ?? payload.message ?? "The saved card could not be used.");
      if (payload.status === "requires_action" && payload.clientSecret) {
        const action = await stripe.handleNextAction({ clientSecret: payload.clientSecret });
        if (action.error)
          throw new Error(action.error.message ?? "The saved card could not be authenticated.");
        const verified = await fetch(
          verifyEndpoint ?? `/api/tables/payment-sessions/${session.publicId}/verify`,
          {
            method: "POST",
            headers: { "content-type": "application/json" },
            body: JSON.stringify({ sessionToken: session.sessionToken }),
          },
        );
        const verification = await verified.json();
        if (!verified.ok || verification.status !== "succeeded")
          throw new Error("Trellis Payments is still processing the saved card.");
      } else if (payload.status !== "succeeded") {
        throw new Error(`The saved card is currently ${payload.status}.`);
      }
      await onComplete();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "The saved card could not be used.");
      setSubmitting(false);
    }
  };

  const submit = async () => {
    if (!stripe || !elements) return;
    setSubmitting(true);
    setError("");
    const result = isCardHold
      ? await stripe.confirmSetup({ elements, redirect: "if_required" })
      : await stripe.confirmPayment({ elements, redirect: "if_required" });
    if (result.error) {
      setError(
        result.error.message ?? "Trellis Payments could not complete the payment step.",
      );
      setSubmitting(false);
      return;
    }
    const verified = await fetch(
      verifyEndpoint ?? "/api/tables/payment-sessions/" + session.publicId + "/verify",
      {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ sessionToken: session.sessionToken }),
      },
    );
    const payload = await verified.json();
    if (!verified.ok || payload.status !== "succeeded") {
      setError(
        payload.message ??
          "Trellis Payments is still processing this payment. Try again in a moment.",
      );
      setSubmitting(false);
      return;
    }
    try {
      await onComplete();
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : "The reservation could not be completed.",
      );
      setSubmitting(false);
    }
  };

  return (
    <div
      className={
        "booking-body booking-details stripe-payment-step reservation-payment-step " +
        context
      }
    >
      <p className="step-label">Secure payment</p>
      <h2>
        {isCardHold
          ? "Save a card for this reservation."
          : "Complete the payment."}
      </h2>
      <div className="stripe-payment-summary">
        <CreditCard />
        <span>
          <strong>
            {isCardHold
              ? "Card hold authorization"
              : session.bookingMethod === "deposit"
                ? "Reservation deposit"
                : "Prepaid reservation"}
          </strong>
          <small>
            {formatMoney(session.amount, session.currency)} · processed by
            Trellis Payments
          </small>
        </span>
      </div>
      {savedPaymentMethods.length > 0 && (
        <div className="saved-payment-options">
          <strong>Use a saved card</strong>
          {savedPaymentMethods.map((method) => (
            <button
              type="button"
              key={method.publicId}
              disabled={submitting}
              onClick={() => void submitSavedMethod(method)}
            >
              <CreditCard />
              <span><b>{method.brand} •••• {method.last4}</b><small>{method.expMonth && method.expYear ? `Expires ${String(method.expMonth).padStart(2, "0")}/${String(method.expYear).slice(-2)}` : "Saved securely"}</small></span>
              <em>{method.isDefault ? "Default" : "Use"}</em>
            </button>
          ))}
          <span>Or enter another card</span>
        </div>
      )}
      <PaymentElement options={{ layout: "tabs" }} />
      {isCardHold && (
        <p className="stripe-payment-disclosure">
          <ShieldCheck /> The card is saved securely and is not charged now.
          The venue may charge up to{" "}
          {formatMoney(session.amount, session.currency)} under the reservation
          policy.
        </p>
      )}
      {error && (
        <p className="booking-error" role="alert">
          {error}
        </p>
      )}
      <Button
        size="lg"
        className="button button--burgundy button--full"
        disabled={!stripe || submitting}
        onClick={() => void submit()}
      >
        {submitting
          ? "Confirming with Trellis Payments…"
          : isCardHold
            ? "Save card and confirm"
            : "Pay " +
              formatMoney(session.amount, session.currency) +
              " and confirm"}
      </Button>
      <button
        className="confirmation-done"
        type="button"
        disabled={submitting}
        onClick={onBack}
      >
        Back to payment options
      </button>
    </div>
  );
}

const formatMoney = (amount: number, currency: string) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
  }).format(amount / 100);
